<script>
  import { onMount, onDestroy } from 'svelte';
  import i18n from "./i18n/i18n";
  import { tourFunctions } from "../tour-functions";
  import Tooltip from "./Tooltip.svelte";
  import AudioPlayIcon from "./icons/AudioPlayIcon.svelte";
  import AudioPauseIcon from "./icons/AudioPauseIcon.svelte";
    import VideoUnmuteIcon from './icons/VideoUnmuteIcon.svelte';
    import VideoMuteIcon from './icons/VideoMuteIcon.svelte';

  export let tour;
  export let playing = true;
  export let resumable = false;
  export let enableSceneAudio = true;
  export let enableDescriptionAudio = true;
  export let onPlayingChange = null;
  export let autoInit = true; // Allow disabling auto-initialization

  let isInitialized = false;
  let hasUserInteracted = false;
  let overlayCreated = false;
  let isPlaying = playing;
  let currentSceneName = null;
  let currentAudioUrl = null;
  let currentAssetId = null; // Track current audio asset ID for continuity
  let audioState = 'idle';
  let audioProgress = {};
  let showingTooltip = false;
  let audioElement;

  /** Apply play/pause from parent `playing` prop only — does not notify parent (preserves mute preference across map/tour). */
  function applyPlayingState(shouldPlay) {
    if (audioElement && currentAudioUrl) {
      if (shouldPlay) {
        playAudio();
        audioState = 'playing';
      } else {
        pauseAudio();
        audioState = 'idle';
      }
    }
    isPlaying = shouldPlay;
  }

  $: hasTourAudio = tour?.scenes?.some(s => s.audioId || s.descriptionAudio?.assetId) || false;
  $: hasCurrentSceneAudio = currentAssetId !== null;
  $: if (isInitialized && playing !== isPlaying) applyPlayingState(playing);
  $: if (tour && hasTourAudio && hasUserInteracted && isInitialized) handleSceneChange();
  $: text = isPlaying ? $i18n.t('tour.pauseAudio') : $i18n.t('tour.playAudio');

  const showTooltip = () => showingTooltip = true;
  const hideTooltip = () => showingTooltip = false;
  const isDev = process.env.NODE_ENV === 'development';
  const getAudioUrl = (key) => isDev ? `https://tours.vntpoint.com/${key}` : `/${key}`;

  const unlockAudio = () => {
    try {
      // Create a simple oscillator to unlock audio context
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Set volume to 0 (silent)
      gainNode.gain.value = 0;
      
      // Start and immediately stop the oscillator
      oscillator.start();
      oscillator.stop(0.001);
      
      // Resume audio context if suspended
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      isInitialized = true;
      console.log("✅ Audio unlocked successfully");
      return true;
    } catch (e) {
      console.error("🔒 Audio unlock failed:", e);
      // Fallback: just mark as initialized
      isInitialized = true;
      return true;
    }
  };

  const attemptAutoplay = async () => {
    console.log('attemptAutoplay called');
    const testAudio = new Audio();
    testAudio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
    testAudio.volume = 0;
    console.log('testAudio created');
    
    const cleanup = () => {
      testAudio.pause();
      testAudio.src = '';
    };
    
    try {
      // On iOS Safari, play() promise can hang indefinitely
      const playPromise = testAudio.play();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Play promise timeout')), 2000)
      );
      
      try {
        await Promise.race([playPromise, timeoutPromise]);
        console.log('testAudio played');
      } catch (playError) {
        if (playError.message === 'Play promise timeout') {
          console.log("🔒 Autoplay blocked (iOS Safari - play() promise hung)");
          cleanup();
          return false;
        }
        throw playError;
      }
      
      // Check immediately if playback actually started (iOS Safari quirk)
      if (testAudio.paused) {
        console.log("🔒 Autoplay blocked (iOS Safari - promise resolved but audio paused)");
        cleanup();
        return false;
      }
      
      // Success - unlock audio context
      cleanup();
      unlockAudio();
      hasUserInteracted = true;
      console.log("✅ Autoplay successful");
      return true;
      
    } catch (e) {
      console.log("🔒 Autoplay failed, will show overlay:", e);
      cleanup();
      return false;
    }
  };

  const createOverlay = () => {
    if (overlayCreated || hasUserInteracted) {
      console.log('Audio - Overlay creation skipped:', { overlayCreated, hasUserInteracted });
      return;
    }
    console.log('Audio - Creating overlay for user interaction');
    const overlay = document.createElement("div");
    overlay.id = "audio-init-overlay";
    overlay.style.cssText = `position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: transparent; z-index: 9999; cursor: pointer;`;

    const gestureHandler = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      hasUserInteracted = true;
      // Let other overlays (tutorial) react to the same first gesture.
      window.dispatchEvent(new CustomEvent("audio:gestureunlock"));
      if (unlockAudio()) {
        overlay.remove();
        overlayCreated = true;
        handleSceneChange();
      }
    };

    overlay.addEventListener("touchstart", gestureHandler, { once: true });
    overlay.addEventListener("click", gestureHandler, { once: true });
    document.body.appendChild(overlay);
    overlayCreated = true;
    console.log('Audio - Overlay created and appended to DOM');
  };

  const playAudio = () => {
    if (!audioElement || audioElement.readyState === 0) return;
    audioElement.play().catch(e => console.error('Failed to play audio:', e));
  };

  const pauseAudio = () => {
    if (!audioElement) return;
    audioElement.pause();
  };

  const stopAudio = () => {
    if (!audioElement) return;
    audioElement.pause();
    audioElement.currentTime = 0;
    // Clear asset tracking when audio is stopped
    currentAssetId = null;
  };

  const loadAudio = async (scene) => {
    console.log('Loading audio for scene:', scene.name);
    
    // Determine which audio to load (scene audio takes precedence)
    let assetId = null;
    let isSceneAudio = false;
    
    if (enableSceneAudio && scene.audioId) {
      assetId = scene.audioId;
      isSceneAudio = true;
    } else if (enableDescriptionAudio && scene.descriptionAudio?.assetId) {
      assetId = scene.descriptionAudio.assetId;
      isSceneAudio = false;
    }
    
    if (!assetId) {
      console.log('No audio to load for this scene');
      // Clear current audio if no audio for this scene
      if (currentAudioUrl) {
        stopAudio();
        currentAudioUrl = null;
        currentAssetId = null;
      }
      currentSceneName = scene.name;
      audioState = 'idle';
      return;
    }
    
    // Check if this is the same audio as currently playing
    if (currentAssetId === assetId && currentAudioUrl) {
      console.log('Same audio detected - continuing playback without interruption');
      
      // Update scene tracking without stopping audio
      currentSceneName = scene.name;
      return;
    }
    
    // Save progress of current audio if resumable
    if (resumable && currentSceneName && audioElement && currentAudioUrl) {
      audioProgress[currentSceneName] = audioElement.currentTime;
    }
    
    // Stop current audio before loading new audio
    console.log('Loading new audio - stopping current audio');
    stopAudio();
    currentAudioUrl = null;
    currentAssetId = null;
    currentSceneName = scene.name;
    audioState = 'idle';
    
    const key = tour.assets?.find(a => a.id === assetId)?.s3objectKey;
    if (!key) {
      console.log('No asset found for audioId:', assetId);
      return;
    }

    const audioUrl = getAudioUrl(key);
    console.log('Loading audio URL:', audioUrl, 'isSceneAudio:', isSceneAudio);
    
    currentAudioUrl = audioUrl;
    currentAssetId = assetId; // Track the asset ID for continuity
    audioState = 'loading';
    
    // Set the audio source and load
    audioElement.src = audioUrl;
    audioElement.loop = Boolean(scene.audioLoop);
    audioElement.load();
    
    // On Android, play() must be called directly from user interaction
    // If user has interacted and we want to play, initiate play() immediately
    // This queues the play within the user interaction context
    if (hasUserInteracted && isPlaying) {
      try {
        // Queue play() call - it will execute when audio is ready
        // This preserves the user interaction context on Android
        audioElement.play().catch(() => {
          // Will be handled by onCanPlayThrough
        });
      } catch (err) {
        // Will be handled by onCanPlayThrough
      }
    }
    
    // Set up event listeners
    const onCanPlayThrough = () => {
      console.log('Audio can play through');
      if (isPlaying) {
        // Only use resumable for scene audio, description audio always starts from beginning
        // Don't resume if audio has already ended
        const savedProgress = audioProgress[currentSceneName];
        const hasEnded = savedProgress && audioElement.duration && savedProgress >= audioElement.duration;
        const start = (resumable && isSceneAudio && savedProgress && !hasEnded) || 0;
        audioElement.currentTime = start;
        
        // Only call play() if not already playing (to avoid duplicate calls)
        if (audioElement.paused) {
          playAudio();
        }
        audioState = 'playing';
      } else {
        audioState = 'idle';
      }
    };
    
    const onEnded = () => {
      console.log('Audio ended');
      audioState = 'idle';
      
      // If this was scene audio and there's description audio, load it
      if (isSceneAudio && enableDescriptionAudio && scene.descriptionAudio?.assetId) {
        loadAudio(scene);
      }
    };
    
    const onError = (e) => {
      console.error('Audio error:', e);
      audioState = 'error';
    };
    
    // Remove existing listeners and add new ones
    audioElement.removeEventListener('canplaythrough', onCanPlayThrough);
    audioElement.removeEventListener('ended', onEnded);
    audioElement.removeEventListener('error', onError);
    
    audioElement.addEventListener('canplaythrough', onCanPlayThrough, { once: true });
    audioElement.addEventListener('ended', onEnded, { once: true });
    audioElement.addEventListener('error', onError, { once: true });
  };

  const handleSceneChange = async () => {
    console.log('handleSceneChange called');
    if (!hasTourAudio) {
      console.log('Scene change blocked - no tour audio');
      return;
    }

    const sceneId = tourFunctions.currentScene.getId();
    console.log('Current scene ID:', sceneId);
    const scene = tour.scenes.find(s => s.id === sceneId);
    if (!scene) {
      console.log('Scene not found for ID:', sceneId);
      return;
    }

    console.log('Found scene:', scene.name, 'audioId:', scene.audioId, 'descriptionAudio:', scene.descriptionAudio?.assetId);

    try {
      await loadAudio(scene);
    } catch (e) {
      console.error('handleSceneChange failed:', e);
    }
  };

  const togglePlay = async () => {
    // If not initialized, try to initialize first
    if (!isInitialized) {
      console.log('Audio - Not initialized, attempting to initialize on button click');
      await init();
      if (!isInitialized) {
        console.log('Audio - Still not initialized after init attempt');
        return;
      }
    }
    
    const next = !isPlaying;
    applyPlayingState(next);
    playing = next;
    if (onPlayingChange) onPlayingChange(next);
  };

  const replayDescriptionAudio = () => {
    const scene = tour.scenes.find(s => s.id === tourFunctions.currentScene.getId());
    if (scene && scene.descriptionAudio?.assetId) {
      // Temporarily disable scene audio to force description audio
      const originalEnableSceneAudio = enableSceneAudio;
      enableSceneAudio = false;
      loadAudio(scene);
      enableSceneAudio = originalEnableSceneAudio;
    }
  };

  onMount(async () => {
    // Only auto-initialize if autoInit is true
    if (autoInit) {
      // Try autoplay first
      const autoplaySuccess = await attemptAutoplay();
      
      // If autoplay failed, create the overlay
      if (!autoplaySuccess) {
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
          createOverlay();
        } else {
          document.addEventListener('DOMContentLoaded', createOverlay, { once: true });
        }
      }
    }
    
    // Happens earlier in the scene load process than krpano:newpano
    window.addEventListener("krpano:xmlcomplete", e => {
      if (e.detail.scene && isInitialized) handleSceneChange();
    });

    // window.addEventListener("krpano:removepano", e => {
    //   stopAudio();
    // });
  });

  onDestroy(() => {
    stopAudio();
    document.getElementById('audio-init-overlay')?.remove();
  });

  // Export init function for manual initialization
  const init = async () => {
    console.log('Audio - init() called, isInitialized:', isInitialized);
    if (isInitialized) {
      console.log('Audio - Already initialized, returning');
      return;
    }
    
    console.log('Audio - Attempting autoplay...');
    // Try autoplay first
    const autoplaySuccess = await attemptAutoplay();
    // If autoplay failed, create the overlay
    if (!autoplaySuccess) {
      console.log('Audio - Autoplay failed, creating overlay');
      if (document.readyState === 'complete' || document.readyState === 'interactive') {
        console.log('Audio - Document ready, creating overlay');
        createOverlay();
      } else {
        console.log('Audio - Document not ready, adding event listener for DOMContentLoaded');
        document.addEventListener('DOMContentLoaded', createOverlay, { once: true });
      }
    } else {
      console.log('Audio - Autoplay successful, loading scene audio');
      // If autoplay succeeded, we should load the current scene audio
      if (hasTourAudio) {
        handleSceneChange();
      }
    }
  };

  export { replayDescriptionAudio, init };
</script>

{#if hasCurrentSceneAudio}
  <div class="container" on:mouseenter={showTooltip} on:mouseleave={hideTooltip}>
    <div class="tooltip-container">
      <Tooltip {text} {showingTooltip} position="right" />
      <button 
        class="audio" 
        on:click={togglePlay} 
        aria-label={text} 
        aria-pressed={isPlaying} 
        disabled={!isInitialized}
      >
        {#if isPlaying}
          <VideoUnmuteIcon size={30} />
        {:else}
          <VideoMuteIcon size={30} />
        {/if}
      </button>
    </div>
  </div>
{/if}

<!-- Single audio element for all audio playback -->
<audio 
  bind:this={audioElement}
  preload="auto"
  crossorigin="anonymous"
  style="display: none;"
/>

<style>
  .container {
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 999;
    margin: 10px 20px;
  }
  .tooltip-container {
    position: relative;
  }
  .audio {
    background: transparent;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: none;
  }
  .audio:hover:not(:disabled) { 
    opacity: 0.8; 
  }
  .audio:disabled { 
    opacity: 0.5; 
    cursor: not-allowed; 
  }
  @media (max-width: 768px) {
    .container { 
      left: auto; 
      right: 0; 
      margin: 10px 0;
    }
    .audio { 
      min-width: 44px; 
      min-height: 44px; 
      padding: 8px; 
    }
  }
</style>
